import React, { useState } from "react";
import cx from "classnames";
import type { Theme } from "@material-ui/core";
import { Drawer, makeStyles, useMediaQuery, useTheme, IconButton } from "@material-ui/core";
import type {
    ISitesRepository,
    IIssuesRepository,
    ISiteScheduleExceptionsRepository,
    ICompaniesRepository,
    IUsersRepository,
    ITeamsRepository,
    ISiteVideoStreamsRepository,
    IPartnersRepository,
} from "App/Modules/Repositories";
import type { IPartnerUserSlim, ISiteSlim } from "App/Modules/Models";
import { Permission } from "App/Modules/Models";
import { faTimes, faEllipsisV } from "@fortawesome/pro-light-svg-icons";

import { faIcon, getUserProfile, hasUserPermissionOnAnyPartner } from "App/ReactComponents/Shared";
import { CreateAnythingButton } from "../CreateAnything/CreateAnythingButton";
import { ScheduleExceptionsDialog } from "../ScheduleExceptions/ScheduleExceptionsDialog";
import { SitesList } from "../SitesList/SitesList";
import { FindUserByCode } from "../FindUserByCode/FindUserByCode";

const drawerWidth = 400;

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: "flex",
        flexDirection: "row-reverse",
    },
    mainContent: {
        width: "100%",
        flexDirection: "row",
    },
    drawer: {
        maxWidth: "100%",
        width: drawerWidth,
        flexShrink: 0,
        zIndex: 1, // Fix for dialogs in drawr opening below relatively positioned main content
    },
    drawerPaper: {
        border: "none",
        maxWidth: "100%",
        width: drawerWidth,
        zIndex: 0,
        padding: theme.spacing(2),
        ".MuiDrawer-docked &": {
            background: "transparent",
            paddingTop: 110,
            // Below two rows fix drawer position when popups are open (mui removes scrollbar and adds padding for .mui-fixed classes)
            // https://github.com/mui-org/material-ui/issues/10000
            boxSizing: "content-box",
            width: drawerWidth - theme.spacing(2),
        },
    },
    temporaryDrawerCloseButton: {
        alignSelf: "flex-end",
    },
    temporaryDrawerOpenButton: {
        position: "absolute",
        top: 0,
        right: 0,
    },
}));

type MainDashboardProps = {
    irUsersRepository: IUsersRepository;
    mmCompaniesRepository: ICompaniesRepository;
    mmIssuesRepository: IIssuesRepository;
    mmSitesRepository: ISitesRepository;
    mmSiteScheduleExceptionsRepository: ISiteScheduleExceptionsRepository;
    mmSiteVideoStreamsRepository: ISiteVideoStreamsRepository;
    mmTeamsRepository: ITeamsRepository;
    mmPartnersRepository: IPartnersRepository;
};

export const MainDashboard = (props: MainDashboardProps) => {
    const classes = useStyles();
    const theme = useTheme();
    const lgWidthUp = useMediaQuery(theme.breakpoints.up("lg"), {
        defaultMatches: true,
    });

    const drawerPaperClass = cx(classes.drawerPaper, "mui-fixed");

    const [open, setOpen] = useState(false);
    const [isScheduleExceptionsDialogOpen, setIsScheduleExceptionsDialogOpen] = useState(false);
    const [scheduleExceptionDialogRequestingUser, setScheduleExceptionDialogRequestingUser] =
        useState<IPartnerUserSlim>(null);
    const [scheduleExceptionDialogSite, setScheduleExceptionDialogSite] = useState<ISiteSlim>(null);

    const handleCloseScheduleExceptionsDialog = () => {
        setIsScheduleExceptionsDialogOpen(false);
        setScheduleExceptionDialogRequestingUser(null);
        setScheduleExceptionDialogSite(null);
    };

    const profile = getUserProfile();
    const showFindUser = hasUserPermissionOnAnyPartner(profile, Permission.PartnerMonitoring);

    return (
        <div className={classes.root}>
            <Drawer
                anchor="right"
                variant={lgWidthUp ? "permanent" : "temporary"}
                className={classes.drawer}
                classes={{ paper: drawerPaperClass }}
                open={open}
                {...(lgWidthUp ? {} : { keepMounted: true })}
                onClose={() => setOpen(false)}
            >
                {!lgWidthUp && (
                    <IconButton
                        className={classes.temporaryDrawerCloseButton}
                        onClick={() => setOpen((oldOpen) => !oldOpen)}
                    >
                        {faIcon(faTimes)}
                    </IconButton>
                )}
                {showFindUser ? (
                    <FindUserByCode
                        irUsersRepository={props.irUsersRepository}
                        onScheduleExceptionsDialogOpen={(user: any, site: any) => {
                            setScheduleExceptionDialogRequestingUser(user);
                            setScheduleExceptionDialogSite(site);
                            setIsScheduleExceptionsDialogOpen(true);
                        }}
                    />
                ) : null}
                <CreateAnythingButton
                    mmCompaniesRepository={props.mmCompaniesRepository}
                    mmSitesRepository={props.mmSitesRepository}
                    mmTeamsRepository={props.mmTeamsRepository}
                    mmPartnersRepository={props.mmPartnersRepository}
                />
            </Drawer>
            <ScheduleExceptionsDialog
                isOpen={isScheduleExceptionsDialogOpen}
                onClose={() => handleCloseScheduleExceptionsDialog()}
                site={scheduleExceptionDialogSite}
                siteScheduleExceptionsRepository={props.mmSiteScheduleExceptionsRepository}
                requestingUser={scheduleExceptionDialogRequestingUser}
            />
            <div className={classes.mainContent}>
                {!lgWidthUp && (
                    <IconButton
                        className={classes.temporaryDrawerOpenButton}
                        onClick={() => setOpen((oldOpen) => !oldOpen)}
                    >
                        {faIcon(open ? faTimes : faEllipsisV)}
                    </IconButton>
                )}
                <SitesList
                    mmIssuesRepository={props.mmIssuesRepository}
                    mmSiteScheduleExceptionsRepository={props.mmSiteScheduleExceptionsRepository}
                    mmSitesRepository={props.mmSitesRepository}
                    mmSiteVideoStreamsRepository={props.mmSiteVideoStreamsRepository}
                />
            </div>
        </div>
    );
};
